/**
 * KOPS-460: Events Admin Page
 * aisearchportal.com/admin/events
 * Navy + Gold design matching ContactsPage
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faPlus, faUsers, faEdit, faCopy, faCheck,
  faExternalLinkAlt, faEnvelope, faPaperPlane, faMapMarkerAlt,
  faClock, faSync, faDollarSign, faLock, faGlobe, faStar
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const emptyForm = {
  title: '', seriesName: 'AI Without Fear', category: 'seminar', description: '',
  eventDate: '', eventTime: '', durationMinutes: '90',
  locationName: '', locationAddress: '', maxCapacity: '50', ticketPrice: '0',
  meetingLink: '', meetingDetails: '', sponsorName: '', sponsorEmail: '', sponsorContactName: '', isPrivate: false,
  ceSchoolName: '', ceSchoolAddress: '', ceSchoolPhone: '',
  ceAdreSchoolNumber: '', ceCourseApprovalNumber: '', ceCreditHours: '',
  cePendingApproval: false, ceInstructorName: '', ceAdminName: '', ceAdminEmail: ''
};

// Offering presets — one click pre-fills a complete, current event (Aug 2026 catalog).
// CE descriptions match the APPROVED amended outlines; never "ADRE-approved" language.
const DSS_DEFAULTS = {
  ceSchoolName: 'Desert Sage Seminars', ceSchoolPhone: '(602) 377-6207',
  ceSchoolAddress: '15029 N. Thompson Peak Pkwy, B111-487, Scottsdale, AZ 85260',
  ceAdreSchoolNumber: 'S06-0002', ceInstructorName: 'Michael Boykin',
  ceAdminName: 'Jon Kichen', ceAdminEmail: 'admin@desertsageseminars.com',
  ceCreditHours: '3', durationMinutes: '180', seriesName: 'CE Course',
  category: 'ce_course', ticketPrice: '25', maxCapacity: '40',
  locationName: 'KairosIQ AI HQ', locationAddress: '2700 S Gilbert Rd, Ste 5, Chandler, AZ 85286',
};
const OFFERING_PRESETS: Record<string, any> = {
  ce_ethical: { ...DSS_DEFAULTS, title: 'Ethical Use of Technology', ceCourseApprovalNumber: 'DSS18358',
    description: 'A 3-hour accredited continuing education course in Real Estate Legal Issues. The law is the curriculum; technology is the fact pattern.\n\n1. Technology in Real Estate Practice: Licensed Activity & Liability\n2. Fair Housing: Where Technology Risk Begins\n3. Algorithmic Bias & Real-World Lawsuits\n4. Transparency & Verification: Why Clients Need a Licensed Professional\n5. Advertising, Marketing & Technology Compliance\n6. Government Policy & Emerging Regulation\n7. Ethical Technology Best Practices for Licensees\n\nEvery module anchors to Arizona statutes, federal law, or controlling case law.' },
  ce_fair_housing: { ...DSS_DEFAULTS, title: 'Fair Housing in the Age of Technology', ceCourseApprovalNumber: 'DSS18796',
    description: 'A 3-hour accredited continuing education course in Fair Housing law, grounded in the federal Fair Housing Act and A.R.S. 41-1491 et seq.\n\n1. The Fair Housing Act & Arizona Law\n2. Discrimination in Practice\n3. Fair Housing Advertising Compliance\n4. Case Law & Enforcement\n5. Steering & Disparate Impact in Modern Practice\n6. Building a Fair Housing Compliance Practice\n\nTechnology appears only as examples within the law taught.' },
  ce_disclosures: { ...DSS_DEFAULTS, title: 'Disclosures in Modern Practice', ceCourseApprovalNumber: 'DSS18797',
    description: 'A 3-hour accredited continuing education course in Arizona disclosure and broker-supervision law.\n\n1. Arizona\u2019s Disclosure Framework\n2. Agency & Advertising Disclosure\n3. The Broker\u2019s Written-Policy & Supervision Duty\n4. Accuracy in the Information Age: The Misrepresentation Duty in Modern Practice\n5. Meeting the Written-Policy Duty\n6. Liability & Accountability\n\nLicensees leave able to build the technology section of the written policy manual Arizona law requires.' },
  ws_ai_employee: { category: 'workshop', seriesName: 'Your AI Employee', title: 'Your AI Employee: Put Agentic AI to Work on Your Own Book of Business',
    durationMinutes: '180', ticketPrice: '0', maxCapacity: '40',
    description: 'The 3-hour hands-on bootcamp. Part 1: train an AI on who you are. Part 2: AI that works without being asked. Part 3: point it at the highest-value data you own \u2014 your past clients and the neighborhoods your closings earned you. Every registrant\u2019s Book of Business Report is built before class; agents open their own report in the room. Sponsor-funded; agents attend free.' },
  ws_claude: { category: 'workshop', seriesName: 'Claude for Real Estate', title: 'Claude for Real Estate: Hands-On',
    durationMinutes: '180', ticketPrice: '0', maxCapacity: '40',
    description: 'Learn how to get more out of Claude. Part 1: set up like a professional \u2014 privacy settings and Claude Projects, built live. Part 2: real work in your voice \u2014 listings, client emails, market summaries. Part 2B: the content engine \u2014 decks that become LinkedIn carousels, the full repurpose chain, a month of content in one sitting. Part 3: the habits that protect your license. Private professional education; not CE. Sponsor-funded; agents attend free.' },
  ws_awf_skills: { category: 'workshop', seriesName: 'AI Without Fear', title: 'AI Without Fear: Practical Skills Workshop',
    durationMinutes: '180', ticketPrice: '0', maxCapacity: '40',
    description: 'How to actually use AI for real estate work, taught on the agents\u2019 own business \u2014 not demos. Prompting with context, listing language, client communication, evaluating output before it publishes, and the six-item pre-publish checklist. Private professional education; not CE. Sponsor-funded; agents attend free.' },
  ws_supervising_joint: { category: 'workshop', seriesName: 'Supervising AI', title: 'Supervising AI \u2014 Joint Broker Session',
    durationMinutes: '180', ticketPrice: '150', maxCapacity: '30',
    description: 'The broker risk & policy workshop, shared room: the enforcement record, the law of supervision (R4-28-1103(A), A.R.S. 32-2151.01), and hands-on drafting \u2014 leadership leaves with a draft AI Use Policy, the Nine-Elements-to-Arizona-Law Map, and a 30-day implementation checklist. $150 per seat; additional attendees from the same brokerage $50 each. Private professional education; not CE.' },
  ws_supervising_private: { category: 'workshop', seriesName: 'Supervising AI', title: 'Supervising AI \u2014 Private Brokerage Workshop',
    durationMinutes: '180', ticketPrice: '500', maxCapacity: '50',
    description: 'The broker risk & policy workshop, exclusively your brokerage, at your office: leadership, staff, and agents together, trained on your own operation. You leave with a draft AI Use Policy for YOUR brokerage, the Nine-Elements-to-Arizona-Law Map, and a 30-day implementation checklist. Private professional education; not CE.' },
  ws_supervising_sponsored: { category: 'workshop', seriesName: 'Supervising AI', title: 'Supervising AI \u2014 Sponsored Broker Session',
    durationMinutes: '180', ticketPrice: '0', maxCapacity: '50',
    description: 'The broker risk & policy workshop for designated brokers, managers, and leadership teams \u2014 hosted and sponsored, so class membership is included for every registrant. The enforcement record, the law of supervision (R4-28-1103(A), A.R.S. 32-2151.01), and hands-on drafting: you leave with a draft AI Use Policy for your brokerage, the Nine-Elements-to-Arizona-Law Map, and a 30-day implementation checklist. Private professional education; not CE.' },
};

const EventsAdminPage: React.FC = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [copiedId, setCopiedId] = useState('');
  const [showEmailBlast, setShowEmailBlast] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedRegs, setSelectedRegs] = useState<Set<string>>(new Set());
  const [showAddReg, setShowAddReg] = useState(false);
  const [regFilter, setRegFilter] = useState('all');
  const [newReg, setNewReg] = useState({ fullName: '', email: '', phone: '', brokerage: '', sendEmail: true });
  const [addingReg, setAddingReg] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'registrations' | 'sponsors' | 'team' | 'notes' | 'survey' | 'email' | 'activity' | 'certificates'>('registrations');
  // KOPS-564 — Generated certificates list (lazy-loaded when certificates tab opens)
  const [generatedCerts, setGeneratedCerts] = useState<any[]>([]);
  const [combinedCert, setCombinedCert] = useState<any>(null);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [eventActivities, setEventActivities] = useState<any[]>([]);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [showAddSponsor, setShowAddSponsor] = useState(false);
  const [newSponsor, setNewSponsor] = useState({ sponsorName: '', sponsorEmail: '', sponsorPhone: '', sponsorCompany: '', sponsorRole: 'venue', notes: '' });
  const [addingSponsor, setAddingSponsor] = useState(false);
  const [sponsorDirectory, setSponsorDirectory] = useState<any[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedTeamRole, setSelectedTeamRole] = useState('staff');
  const [blastSubject, setBlastSubject] = useState('');
  const [blastBody, setBlastBody] = useState('');
  const [blastRecipients, setBlastRecipients] = useState({ sponsors: true, team: false, ccTeam: true, registrants: false });
  const [directEmails, setDirectEmails] = useState('');
  const [blastAttachments, setBlastAttachments] = useState<File[]>([]);
  const [sendingBlast, setSendingBlast] = useState(false);
  const [sessionNotes, setSessionNotes] = useState<any>({ sessionSummary: '', questionsAsked: '', audienceReception: '', keyOutcomes: '', followUpNeeded: '', courseOutline: '' });
  const [savingNotes, setSavingNotes] = useState(false);
  const [surveyResults, setSurveyResults] = useState<any>(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [sendingSurvey, setSendingSurvey] = useState(false);

  useEffect(() => {
    fetchEvents();
    axiosInstance.get('/events/sponsors/directory').then(res => setSponsorDirectory(res.data?.sponsors || [])).catch(() => {});
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('/events/all');
      if (response.data.success) {
        setEvents(response.data.events);
        // Auto-select event from URL param
        const params = new URLSearchParams(window.location.search);
        const urlEventId = params.get('eventId');
        if (urlEventId && !selected) {
          const match = response.data.events.find((e: any) => e.id === urlEventId);
          if (match) selectEvent(match);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // KOPS-564 — load generated certificates for the selected event
  const fetchGeneratedCerts = async () => {
    if (!selected?.id) return;
    setLoadingCerts(true);
    try {
      const res = await axiosInstance.get(`/events/${selected.id}/certificates`);
      if (res.data?.success) {
        setGeneratedCerts(res.data.certificates || []);
        setCombinedCert(res.data.combined || null);
      }
    } catch (err) {
      console.error('Failed to fetch generated certificates:', err);
    } finally {
      setLoadingCerts(false);
    }
  };

  // Auto-fetch certificates whenever the certificates tab is opened or selected event changes
  useEffect(() => {
    if (activeDetailTab === 'certificates' && selected?.id) {
      fetchGeneratedCerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDetailTab, selected?.id]);

  const selectEvent = async (event: any) => {
    setSelected(event);
    // Update URL so refresh preserves selection
    const url = new URL(window.location.href);
    url.searchParams.set('eventId', event.id);
    window.history.replaceState({}, '', url.toString());
    setRegsLoading(true);
    setShowForm(false);
    setShowEmailBlast(false);
    setSelectedRegs(new Set());
    setRegFilter('all');
    setActiveDetailTab('registrations');
    // Load sponsors and team for this event
    axiosInstance.get(`/events/${event.id}/sponsors`).then(res => setSponsors(res.data?.sponsors || [])).catch(() => setSponsors([]));
    axiosInstance.get(`/events/${event.id}/team`).then(res => setTeam(res.data?.team || [])).catch(() => setTeam([]));
    try {
      const response = await axiosInstance.get(`/events/${event.id}/registrations`);
      if (response.data.success) setRegistrations(response.data.registrations);
    } catch (err) { setRegistrations([]); }
    finally { setRegsLoading(false); }
    // Load session notes
    try {
      const notesRes = await axiosInstance.get(`/events/${event.id}/session-notes`);
      if (notesRes.data.success && notesRes.data.notes) {
        setSessionNotes({
          sessionSummary: notesRes.data.notes.session_summary || '',
          questionsAsked: notesRes.data.notes.questions_asked || '',
          audienceReception: notesRes.data.notes.audience_reception || '',
          keyOutcomes: notesRes.data.notes.key_outcomes || '',
          followUpNeeded: notesRes.data.notes.follow_up_needed || '',
          courseOutline: notesRes.data.notes.course_outline || ''
        });
      } else {
        setSessionNotes({ sessionSummary: '', questionsAsked: '', audienceReception: '', keyOutcomes: '', followUpNeeded: '', courseOutline: '' });
      }
    } catch { setSessionNotes({ sessionSummary: '', questionsAsked: '', audienceReception: '', keyOutcomes: '', followUpNeeded: '', courseOutline: '' }); }
  };

  const openCreate = () => {
    setFormData(emptyForm);
    setEditMode(false);
    setShowForm(true);
    setSelected(null);
  };

  const openEdit = () => {
    if (!selected) return;
    const dt = new Date(selected.event_date);
    setFormData({
      title: selected.title, seriesName: selected.series_name, category: selected.category,
      description: selected.description || '', eventDate: dt.toISOString().slice(0, 16),
      eventTime: selected.event_time, durationMinutes: String(selected.duration_minutes),
      locationName: selected.location_name, locationAddress: selected.location_address,
      meetingLink: selected.meeting_link || '', meetingDetails: selected.meeting_details || '',
      maxCapacity: String(selected.max_capacity), ticketPrice: String(selected.ticket_price || 0),
      sponsorName: selected.sponsor_name || '', sponsorEmail: selected.sponsor_email || '',
      sponsorContactName: selected.sponsor_contact_name || '', isPrivate: selected.is_private,
      ceSchoolName: selected.ce_school_name || '', ceSchoolAddress: selected.ce_school_address || '',
      ceSchoolPhone: selected.ce_school_phone || '', ceAdreSchoolNumber: selected.ce_adre_school_number || '',
      ceCourseApprovalNumber: selected.ce_course_approval_number || '', ceCreditHours: selected.ce_credit_hours || '',
      cePendingApproval: selected.ce_pending_approval || false, ceInstructorName: selected.ce_instructor_name || '',
      ceAdminName: selected.ce_admin_name || '', ceAdminEmail: selected.ce_admin_email || ''
    });
    setEditMode(true);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title, seriesName: formData.seriesName, category: formData.category,
        description: formData.description, eventDate: formData.eventDate, eventTime: formData.eventTime,
        durationMinutes: parseInt(formData.durationMinutes), locationName: formData.locationName,
        locationAddress: formData.locationAddress, meetingLink: formData.meetingLink || undefined, meetingDetails: formData.meetingDetails || undefined,
        maxCapacity: parseInt(formData.maxCapacity), ticketPrice: parseFloat(formData.ticketPrice),
        sponsorName: formData.sponsorName || undefined, sponsorEmail: formData.sponsorEmail || undefined,
        sponsorContactName: formData.sponsorContactName || undefined, isPrivate: formData.isPrivate,
        ceSchoolName: formData.ceSchoolName || undefined, ceSchoolAddress: formData.ceSchoolAddress || undefined,
        ceSchoolPhone: formData.ceSchoolPhone || undefined, ceAdreSchoolNumber: formData.ceAdreSchoolNumber || undefined,
        ceCourseApprovalNumber: formData.ceCourseApprovalNumber || undefined, ceCreditHours: formData.ceCreditHours || undefined,
        cePendingApproval: formData.cePendingApproval, ceInstructorName: formData.ceInstructorName || undefined,
        ceAdminName: formData.ceAdminName || undefined, ceAdminEmail: formData.ceAdminEmail || undefined
      };

      if (editMode && selected) {
        await axiosInstance.put(`/events/${selected.id}`, payload);
        setAlert({ type: 'success', message: 'Event updated!' });
      } else {
        const res = await axiosInstance.post('/events/create', payload);
        setAlert({ type: 'success', message: `Event created! ${res.data.link}` });
      }
      setShowForm(false);
      // Refresh events list and re-select current event with fresh data
      try {
        const response = await axiosInstance.get('/events/all');
        if (response.data.success) {
          setEvents(response.data.events);
          if (editMode && selected) {
            const updated = response.data.events.find((e: any) => e.id === selected.id);
            if (updated) setSelected(updated);
          }
        }
      } catch { fetchEvents(); }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed' });
    } finally { setSubmitting(false); }
  };

  const copyLink = (id: string) => {
    navigator.clipboard.writeText(`https://www.kairosiq.ai/events/${id}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(''), 2000);
  };

  const handleStatusChange = async (regId: string, status: string) => {
    try {
      await axiosInstance.put(`/events/registration/${regId}/status`, { status });
      if (selected) selectEvent(selected);
    } catch (err) { setAlert({ type: 'danger', message: 'Failed to update' }); }
  };

  const handleAddReg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setAddingReg(true);
    try {
      await axiosInstance.post('/events/register', {
        eventId: selected.id, fullName: newReg.fullName, email: newReg.email,
        phone: newReg.phone, brokerage: newReg.brokerage || undefined, skipEmail: !newReg.sendEmail
      });
      setNewReg({ fullName: '', email: '', phone: '', brokerage: '', sendEmail: true });
      setShowAddReg(false);
      selectEvent(selected);
      fetchEvents();
    } catch (err: any) { setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed' }); }
    finally { setAddingReg(false); }
  };

  const handleEmailBlast = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const payload: any = { subject: emailSubject, message: emailMessage };
      if (selectedRegs.size > 0) {
        payload.registrationIds = Array.from(selectedRegs);
      }
      const res = await axiosInstance.post(`/events/${selected.id}/email-blast`, payload);
      setAlert({ type: 'success', message: `Email sent to ${res.data.sent} registrants` });
      setShowEmailBlast(false);
      setSelectedRegs(new Set());
    } catch (err: any) { setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed' }); }
    finally { setSending(false); }
  };

  const toggleReg = (id: string) => {
    setSelectedRegs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const getFilteredRegs = () => registrations.filter((r: any) => {
    if (regFilter === 'all') return r.status !== 'cancelled';
    if (regFilter === 'attended') return r.attended;
    if (regFilter === 'registered') return !r.attended && r.status === 'registered';
    return r.status === regFilter;
  });

  const toggleAllRegs = () => {
    const filtered = getFilteredRegs();
    if (selectedRegs.size === filtered.length) {
      setSelectedRegs(new Set());
    } else {
      setSelectedRegs(new Set(filtered.map((r: any) => r.id)));
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await axiosInstance.post(`/events/${selected.id}/session-notes`, sessionNotes);
      setAlert({ type: 'success', message: 'Session notes saved! AI context updated.' });
    } catch (err: any) { setAlert({ type: 'danger', message: 'Failed to save notes' }); }
    finally { setSavingNotes(false); }
  };

  const loadTeam = async () => {
    if (!selected) return;
    try {
      const res = await axiosInstance.get(`/events/${selected.id}/team`);
      setTeam(res.data?.team || []);
    } catch { setTeam([]); }
  };

  const loadEmployees = async () => {
    try {
      const res = await axiosInstance.get('/events/internal/employees');
      setEmployees(res.data?.employees || []);
    } catch { setEmployees([]); }
  };

  const handleAddTeamMember = async () => {
    if (!selected || !selectedEmployee) return;
    try {
      await axiosInstance.post(`/events/${selected.id}/team`, { userId: selectedEmployee, role: selectedTeamRole });
      setSelectedEmployee('');
      loadTeam();
    } catch (err) { console.error('Failed to add team member:', err); }
  };

  const handleRemoveTeamMember = async (teamId: string) => {
    try {
      await axiosInstance.delete(`/events/team/${teamId}`);
      loadTeam();
    } catch (err) { console.error('Failed to remove:', err); }
  };

  const handleSendBlast = async () => {
    if (!selected || !blastSubject || !blastBody) return;
    setSendingBlast(true);
    try {
      const payload: any = { subject: blastSubject, body: blastBody, recipients: blastRecipients };
      if (directEmails.trim()) {
        payload.directEmails = directEmails.split(',').map((e: string) => e.trim()).filter((e: string) => e.includes('@'));
      }

      let res;
      if (blastAttachments.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('data', JSON.stringify(payload));
        blastAttachments.forEach(f => formData.append('attachments', f));
        res = await axiosInstance.post(`/events/${selected.id}/email-blast`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await axiosInstance.post(`/events/${selected.id}/email-blast`, payload);
      }
      window.alert(`Email sent! ${res.data?.sent || 0} delivered, ${res.data?.totalCC || 0} CC'd`);
      setBlastSubject(''); setBlastBody(''); setDirectEmails(''); setBlastAttachments([]);
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Failed to send');
    }
    setSendingBlast(false);
  };

  const loadSponsors = async () => {
    if (!selected) return;
    try {
      const res = await axiosInstance.get(`/events/${selected.id}/sponsors`);
      setSponsors(res.data?.sponsors || []);
    } catch { setSponsors([]); }
  };

  const handleAddSponsor = async () => {
    if (!selected || !newSponsor.sponsorName) return;
    setAddingSponsor(true);
    try {
      await axiosInstance.post(`/events/${selected.id}/sponsors`, newSponsor);
      setNewSponsor({ sponsorName: '', sponsorEmail: '', sponsorPhone: '', sponsorCompany: '', sponsorRole: 'venue', notes: '' });
      setShowAddSponsor(false);
      loadSponsors();
    } catch (err) { console.error('Failed to add sponsor:', err); }
    setAddingSponsor(false);
  };

  const handleDeleteSponsor = async (sponsorId: string) => {
    if (!window.confirm('Remove this sponsor?')) return;
    try {
      await axiosInstance.delete(`/events/sponsors/${sponsorId}`);
      loadSponsors();
    } catch (err) { console.error('Failed to delete sponsor:', err); }
  };

  const loadSurveyResults = async () => {
    if (!selected) return;
    setSurveyLoading(true);
    try {
      const res = await axiosInstance.get(`/events/${selected.id}/survey-results`);
      if (res.data.success) setSurveyResults(res.data);
    } catch { setSurveyResults(null); }
    finally { setSurveyLoading(false); }
  };

  const handleSendSurvey = async () => {
    if (!selected) return;
    setSendingSurvey(true);
    try {
      const payload: any = {};
      if (selectedRegs.size > 0) {
        payload.registrationIds = Array.from(selectedRegs);
      }
      const res = await axiosInstance.post(`/events/${selected.id}/send-survey`, payload);
      setAlert({ type: 'success', message: `Survey sent to ${res.data.sent} participants` });
    } catch (err: any) { setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send survey' }); }
    finally { setSendingSurvey(false); }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const getCatColor = (c: string) => c === 'workshop' ? colors.orange : c === 'ce_course' ? colors.blue : colors.green;

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0' }}>
        <Container fluid className="px-4 d-flex justify-content-between align-items-center">
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>/ Events</span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/admin/subscribers" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Subscribers</a>
            <a href="/admin/team" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Team</a>
            <a href="/admin/contacts" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Contacts</a>
            <a href="/admin/events" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '13px', padding: '6px 12px', fontWeight: 600 }}>Events</a>
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Stats Strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '16px 24px' }}>
        <div className="d-flex gap-4 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: colors.blue }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{events.length}</span>
            <span style={{ color: colors.muted, fontSize: '12px' }}>Total Events</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faUsers} style={{ color: colors.green }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{events.reduce((s, e) => s + (e.current_registrations || 0), 0)}</span>
            <span style={{ color: colors.muted, fontSize: '12px' }}>Total Registrations</span>
          </div>
          <div className="ms-auto">
            <Button size="sm" onClick={openCreate} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '12px', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> New Event
            </Button>
          </div>
        </div>
      </div>

      {/* Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 130px)' }}>

        {/* Left - Event List */}
        <div style={{ width: '380px', minWidth: '380px', background: colors.navy2, borderRight: `1px solid ${colors.navy3}`, overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
          ) : events.map(event => (
            <div key={event.id} onClick={() => selectEvent(event)}
              style={{
                padding: '14px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                background: selected?.id === event.id ? colors.navy3 : 'transparent',
                borderLeft: selected?.id === event.id ? `3px solid ${colors.gold}` : '3px solid transparent'
              }}>
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.white, fontWeight: 600, fontSize: '14px' }}>{event.title}</div>
                  <div style={{ color: colors.muted, fontSize: '12px', marginTop: '2px' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" style={{ fontSize: '10px' }} />{formatDate(event.event_date)} | {event.event_time}
                  </div>
                  <div style={{ color: colors.muted, fontSize: '11px' }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '10px' }} />{event.location_name}
                  </div>
                </div>
                <div className="text-end">
                  <div style={{ color: colors.white, fontWeight: 700, fontSize: '14px' }}>{event.current_registrations}/{event.max_capacity}</div>
                  <div className="d-flex gap-1 mt-1">
                    <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', background: getCatColor(event.category), color: 'white', fontWeight: 600, textTransform: 'uppercase' }}>
                      {event.category === 'ce_course' ? 'CE' : event.category}
                    </span>
                    {event.ticket_price > 0 && <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', background: colors.gold, color: colors.navy, fontWeight: 700 }}>${event.ticket_price}</span>}
                    {event.is_private && <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', background: '#555', color: '#ccc' }}>Private</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Panel */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#eef1f6' }}>
          {showForm ? (
            /* Create/Edit Form */
            <div style={{ padding: '24px', maxWidth: '700px' }}>
              <h4 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', marginBottom: '20px' }}>
                {editMode ? 'Edit Event' : 'Create New Event'}
              </h4>
              <form onSubmit={handleSubmit}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e9ecef' }}>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Title *</Form.Label>
                        <Form.Control required size="sm" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} /></Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Quick-Fill Offering</Form.Label>
                        <Form.Select size="sm" defaultValue="" onChange={e => {
                          const k = e.target.value;
                          if (k && OFFERING_PRESETS[k]) setFormData({ ...emptyForm, ...OFFERING_PRESETS[k] });
                        }}>
                          <option value="">Choose an offering…</option>
                          <optgroup label="CE Courses (accredited)">
                            <option value="ce_ethical">CE · Ethical Use of Technology</option>
                            <option value="ce_fair_housing">CE · Fair Housing in the Age of Technology</option>
                            <option value="ce_disclosures">CE · Disclosures in Modern Practice</option>
                          </optgroup>
                          <optgroup label="3-Hour AI Workshops">
                            <option value="ws_ai_employee">Workshop · Your AI Employee</option>
                            <option value="ws_claude">Workshop · Claude for Real Estate</option>
                            <option value="ws_awf_skills">Workshop · AI Without Fear: Practical Skills</option>
                          </optgroup>
                          <optgroup label="Broker Workshops">
                            <option value="ws_supervising_joint">Broker · Supervising AI (Joint, $150/seat)</option>
                            <option value="ws_supervising_private">Broker · Supervising AI (Private, $500)</option>
                            <option value="ws_supervising_sponsored">Broker · Supervising AI (Sponsored, free to attend)</option>
                          </optgroup>
                        </Form.Select></Form.Group>
                      <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Category *</Form.Label>
                        <Form.Select size="sm" value={formData.category} onChange={e => {
                          const cat = e.target.value;
                          const updates: any = { category: cat };
                          if (cat === 'ce_course') {
                            // Auto-fill Desert Sage defaults for CE courses
                            if (!formData.ceSchoolName) updates.ceSchoolName = 'Desert Sage Seminars';
                            if (!formData.ceSchoolPhone) updates.ceSchoolPhone = '(602) 377-6207';
                            if (!formData.ceAdreSchoolNumber) updates.ceAdreSchoolNumber = 'S06-0002';
                            if (!formData.ceCreditHours) updates.ceCreditHours = '3';
                            if (!formData.durationMinutes || formData.durationMinutes === '90') updates.durationMinutes = '180';
                            if (!formData.ticketPrice || formData.ticketPrice === '0') updates.ticketPrice = '25';
                            if (!formData.seriesName || formData.seriesName === 'AI Without Fear') updates.seriesName = 'CE Course';
                          }
                          setFormData({ ...formData, ...updates });
                        }}>
                          <option value="seminar">Seminar</option><option value="workshop">Workshop</option><option value="ce_course">CE Course</option>
                        </Form.Select></Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Description</Form.Label>
                    <Form.Control as="textarea" rows={3} size="sm" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} /></Form.Group>
                  <Row>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Date *</Form.Label>
                      <Form.Control required type="datetime-local" size="sm" value={formData.eventDate} onChange={e => setFormData({ ...formData, eventDate: e.target.value })} /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Display Time *</Form.Label>
                      <Form.Control required size="sm" value={formData.eventTime} onChange={e => setFormData({ ...formData, eventTime: e.target.value })} placeholder="10:00 AM - 1:00 PM MST" /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Duration (min)</Form.Label>
                      <Form.Control type="number" size="sm" value={formData.durationMinutes} onChange={e => setFormData({ ...formData, durationMinutes: e.target.value })} /></Form.Group></Col>
                  </Row>
                  <Row>
                    <Col md={6}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Location *</Form.Label>
                      <Form.Control required size="sm" value={formData.locationName} onChange={e => setFormData({ ...formData, locationName: e.target.value })} /></Form.Group></Col>
                    <Col md={6}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Address *</Form.Label>
                      <Form.Control required size="sm" value={formData.locationAddress} onChange={e => setFormData({ ...formData, locationAddress: e.target.value })} /></Form.Group></Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Meeting Link (Zoom/Webex/Teams)</Form.Label>
                        <Form.Control size="sm" value={formData.meetingLink} onChange={e => setFormData({ ...formData, meetingLink: e.target.value })} placeholder="https://us05web.zoom.us/j/83011644937?pwd=..." />
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Join URL — shown as clickable button in confirmation email</div>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Meeting Details (Full Invite)</Form.Label>
                        <Form.Control as="textarea" rows={5} size="sm" value={formData.meetingDetails} onChange={e => setFormData({ ...formData, meetingDetails: e.target.value })} placeholder="Paste full Zoom/Webex invite here — Meeting ID, Passcode, dial-in numbers, etc." style={{ fontFamily: 'monospace', fontSize: '12px' }} />
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Full meeting details included in confirmation email below the join button</div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Capacity</Form.Label>
                      <Form.Control type="number" size="sm" value={formData.maxCapacity} onChange={e => setFormData({ ...formData, maxCapacity: e.target.value })} /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Price ($)</Form.Label>
                      <Form.Control type="number" step="0.01" size="sm" value={formData.ticketPrice} onChange={e => setFormData({ ...formData, ticketPrice: e.target.value })} /></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Series</Form.Label>
                      <Form.Control size="sm" value={formData.seriesName} onChange={e => setFormData({ ...formData, seriesName: e.target.value })} /></Form.Group></Col>
                  </Row>
                  <hr />
                  <p style={{ fontSize: '12px', fontWeight: 700, color: '#666' }}>Sponsor (Optional)</p>
                  <Row>
                    <Col md={4}><Form.Control size="sm" placeholder="Sponsor Name" className="mb-2" value={formData.sponsorName} onChange={e => setFormData({ ...formData, sponsorName: e.target.value })} /></Col>
                    <Col md={4}><Form.Control size="sm" placeholder="Contact Name" className="mb-2" value={formData.sponsorContactName} onChange={e => setFormData({ ...formData, sponsorContactName: e.target.value })} /></Col>
                    <Col md={4}><Form.Control size="sm" type="email" placeholder="Sponsor Email" className="mb-2" value={formData.sponsorEmail} onChange={e => setFormData({ ...formData, sponsorEmail: e.target.value })} /></Col>
                  </Row>
                  {/* CE Course Fields — shown only when category is ce_course */}
                  {formData.category === 'ce_course' && (
                    <div style={{ background: '#fff8e1', borderRadius: '8px', padding: '16px', marginTop: '12px', border: '1px solid #ffe082' }}>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: '#C9952A', marginBottom: '8px' }}>
                        CE Course Information (Required per ADRE R4-28-502)
                      </p>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>Instructor Name *</Form.Label>
                            <Form.Control required size="sm" value={formData.ceInstructorName} onChange={e => setFormData({ ...formData, ceInstructorName: e.target.value })} placeholder="Instructor full name" /></Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>School Name *</Form.Label>
                            <Form.Control required size="sm" value={formData.ceSchoolName} onChange={e => setFormData({ ...formData, ceSchoolName: e.target.value })} placeholder="ADRE-approved school name" /></Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>Administrator Name</Form.Label>
                            <Form.Control size="sm" value={formData.ceAdminName} onChange={e => setFormData({ ...formData, ceAdminName: e.target.value })} placeholder="Administrator full name" /></Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>Administrator Email</Form.Label>
                            <Form.Control size="sm" type="email" value={formData.ceAdminEmail} onChange={e => setFormData({ ...formData, ceAdminEmail: e.target.value })} placeholder="admin@school.com" /></Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>School Phone *</Form.Label>
                            <Form.Control required size="sm" value={formData.ceSchoolPhone} onChange={e => setFormData({ ...formData, ceSchoolPhone: e.target.value })} placeholder="(602) 555-1234" /></Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>ADRE School #</Form.Label>
                            <Form.Control size="sm" value={formData.ceAdreSchoolNumber} onChange={e => setFormData({ ...formData, ceAdreSchoolNumber: e.target.value })} placeholder="S06-0002" /></Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>Course Approval #</Form.Label>
                            <Form.Control size="sm" value={formData.ceCourseApprovalNumber} onChange={e => setFormData({ ...formData, ceCourseApprovalNumber: e.target.value })} placeholder="CE-2026-0142" /></Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2"><Form.Label style={{ fontSize: '11px', fontWeight: 600 }}>CE Credit Hours *</Form.Label>
                            <Form.Control required size="sm" type="number" step="0.5" value={formData.ceCreditHours} onChange={e => setFormData({ ...formData, ceCreditHours: e.target.value })} placeholder="3.0" /></Form.Group>
                        </Col>
                      </Row>
                      <Form.Check type="checkbox" id="ce-pending" label="Course pending ADRE approval (will show disclaimer on event page)" checked={formData.cePendingApproval} onChange={e => setFormData({ ...formData, cePendingApproval: e.target.checked })} style={{ fontSize: '12px' }} className="mt-1" />
                    </div>
                  )}

                  <Form.Check type="checkbox" id="private-check" label="Private (direct link only)" checked={formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })} style={{ fontSize: '13px' }} className="mt-2" />
                </div>
                <div className="d-flex gap-2 mt-3">
                  <Button type="submit" disabled={submitting} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                    {submitting ? <Spinner size="sm" /> : editMode ? 'Save Changes' : 'Create Event'}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </div>
          ) : !selected ? (
            <div style={{ padding: '24px' }}>
              <h4 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', marginBottom: '20px' }}>
                Event <span style={{ color: colors.gold }}>Dashboard</span>
              </h4>

              {/* Quick Stats */}
              <Row className="g-3 mb-4">
                {[
                  { label: 'Upcoming', value: events.filter(e => e.status === 'upcoming').length, color: colors.blue, icon: faCalendarAlt },
                  { label: 'Total Registrations', value: events.reduce((s: number, e: any) => s + (e.current_registrations || 0), 0), color: colors.green, icon: faUsers },
                  { label: 'Paid Events', value: events.filter(e => e.ticket_price > 0).length, color: colors.gold, icon: faDollarSign },
                  { label: 'Completed', value: events.filter(e => e.status === 'completed').length, color: '#8e44ad', icon: faCheck }
                ].map((s, i) => (
                  <Col md={3} key={i}>
                    <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', borderTop: `3px solid ${s.color}` }}>
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: '16px' }} />
                        <span style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{s.value}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.label}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Upcoming Events */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '20px' }}>
                <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.gold }} />Upcoming Events
                </h6>
                {events.filter(e => e.status === 'upcoming').length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '10px 0' }}>No upcoming events scheduled</p>
                ) : (
                  events.filter(e => e.status === 'upcoming').sort((a: any, b: any) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime()).map((event: any) => (
                    <div key={event.id} onClick={() => selectEvent(event)}
                      style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', borderRadius: '6px', marginBottom: '4px' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontWeight: 600, color: colors.navy, fontSize: '14px' }}>{event.title}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" style={{ fontSize: '10px' }} />{formatDate(event.event_date)} | {event.event_time} | {event.location_name}
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontWeight: 700, color: colors.navy }}>{event.current_registrations}/{event.max_capacity}</div>
                          <div className="d-flex gap-1">
                            {event.ticket_price > 0 && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: colors.gold, color: colors.navy, fontWeight: 700 }}>${event.ticket_price}</span>}
                            {event.is_private && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: '#eee', color: '#666' }}>Private</span>}
                          </div>
                        </div>
                      </div>
                      {/* Capacity bar */}
                      <div style={{ marginTop: '8px', background: '#eee', borderRadius: '4px', height: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min((event.current_registrations / event.max_capacity) * 100, 100)}%`, background: event.current_registrations >= event.max_capacity ? colors.red : colors.green, height: '100%', borderRadius: '4px' }} />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Recent Completed */}
              {events.filter(e => e.status === 'completed').length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                    <FontAwesomeIcon icon={faCheck} className="me-2" style={{ color: colors.green }} />Past Events
                  </h6>
                  {events.filter(e => e.status === 'completed').map((event: any) => (
                    <div key={event.id} onClick={() => selectEvent(event)}
                      style={{ padding: '10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', borderRadius: '6px' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontWeight: 600, color: '#555', fontSize: '13px' }}>{event.title}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{formatDate(event.event_date)} | {event.location_name}</div>
                        </div>
                        <div style={{ fontWeight: 600, color: '#888', fontSize: '13px' }}>{event.current_registrations} attended</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '24px' }}>
              {/* Event Header */}
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', margin: 0 }}>{selected.title}</h3>
                    <div className="d-flex gap-3 mt-2" style={{ fontSize: '13px', color: '#555' }}>
                      <span><FontAwesomeIcon icon={faCalendarAlt} className="me-1" style={{ color: colors.gold }} />{formatDate(selected.event_date)}</span>
                      <span><FontAwesomeIcon icon={faClock} className="me-1" style={{ color: colors.gold }} />{selected.event_time}</span>
                      <span><FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ color: colors.gold }} />{selected.location_name}</span>
                    </div>
                    {selected.description && <p style={{ color: '#666', fontSize: '13px', marginTop: '8px', lineHeight: 1.6 }}>{selected.description.substring(0, 150)}...</p>}
                  </div>
                  <div className="text-end">
                    <div style={{ fontSize: '28px', fontWeight: 700, color: colors.navy, fontFamily: "'Bebas Neue', sans-serif" }}>
                      {selected.current_registrations}/{selected.max_capacity}
                    </div>
                    <span style={{ fontSize: '11px', color: '#666' }}>registered</span>
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <Button size="sm" onClick={openEdit} style={{ background: colors.navy, border: 'none', fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                  </Button>
                  <Button size="sm" onClick={() => copyLink(selected.id)} variant="outline-dark" style={{ fontSize: '12px' }}>
                    {copiedId === selected.id ? <><FontAwesomeIcon icon={faCheck} className="me-1" /> Copied!</> : <><FontAwesomeIcon icon={faCopy} className="me-1" /> Copy Link</>}
                  </Button>
                  <Button size="sm" onClick={() => window.open(`https://www.kairosiq.ai/events/${selected.id}`, '_blank')} variant="outline-dark" style={{ fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Preview
                  </Button>
                  <Button size="sm" onClick={() => { navigator.clipboard.writeText(`https://app.kairosiq.ai/events/${selected.id}/survey`); setAlert({ type: 'success', message: 'Survey link copied!' }); }} variant="outline-dark" style={{ fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faStar} className="me-1" /> Copy Survey Link
                  </Button>
                  <Button size="sm" onClick={() => { setEmailSubject(''); setEmailMessage(''); setShowEmailBlast(true); }}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '12px', fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                    {selectedRegs.size > 0 ? `Email Selected (${selectedRegs.size})` : `Email All (${registrations.filter((r: any) => r.status !== 'cancelled').length})`}
                  </Button>
                  <Button size="sm" disabled={sendingSurvey} variant="outline-dark" style={{ fontSize: '12px' }}
                    onClick={() => {
                      const count = selectedRegs.size > 0 ? selectedRegs.size : registrations.filter((r: any) => r.status !== 'cancelled').length;
                      if (window.confirm(`Send survey to ${count} ${selectedRegs.size > 0 ? 'selected' : 'all'} registrant(s)?`)) {
                        handleSendSurvey();
                      }
                    }}>
                    {sendingSurvey ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faStar} className="me-1" />
                    {selectedRegs.size > 0 ? `Send Survey (${selectedRegs.size})` : 'Send Survey'}</>}
                  </Button>
                </div>
              </div>

              {/* Email Blast Inline */}
              {showEmailBlast && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: `1px solid ${colors.gold}` }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                    {selectedRegs.size > 0 ? `Email ${selectedRegs.size} Selected Registrants` : `Email All ${registrations.filter((r: any) => r.status !== 'cancelled').length} Registrants`}
                  </h6>
                  <input placeholder="Subject" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                    style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '14px', marginBottom: '8px', outline: 'none' }} />
                  <textarea placeholder={`Hi {{name}},\n\n...`} value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={5}
                    style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '14px', marginBottom: '8px', outline: 'none', resize: 'vertical' }} />
                  <div className="d-flex gap-2">
                    <Button size="sm" onClick={handleEmailBlast} disabled={sending || !emailSubject || !emailMessage}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                      {sending ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Send</>}
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setShowEmailBlast(false)}>Cancel</Button>
                  </div>
                  <small style={{ color: '#999' }}>Use <code>{'{{name}}'}</code> to personalize.</small>
                </div>
              )}

              {/* Detail Tabs */}
              <div className="d-flex gap-1 mb-3">
                {[
                  { key: 'registrations', label: `Registrations (${registrations.length})` },
                  { key: 'sponsors', label: `Sponsors (${sponsors.length})` },
                  { key: 'team', label: `Team (${team.length})` },
                  { key: 'certificates', label: 'Certificates' },
                  { key: 'email', label: 'Email Blast' },
                  { key: 'notes', label: 'Session Notes' },
                  { key: 'survey', label: 'Survey Results' },
                  { key: 'activity', label: 'Activity Log' }
                ].map(tab => (
                  <button key={tab.key} onClick={() => {
                    setActiveDetailTab(tab.key as any);
                    if (tab.key === 'survey') loadSurveyResults();
                    if (tab.key === 'sponsors') loadSponsors();
                    if (tab.key === 'team') { loadTeam(); loadEmployees(); }
                    if (tab.key === 'activity' && selected) {
                      axiosInstance.get(`/events/${selected.id}/activities`).then(r => {
                        if (r.data.success) setEventActivities(r.data.activities || []);
                      }).catch(() => setEventActivities([]));
                    }
                  }}
                    style={{ background: activeDetailTab === tab.key ? colors.gold : '#f0f0f0', color: activeDetailTab === tab.key ? colors.navy : '#666', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Certificates Tab — KOPS-564 */}
              {activeDetailTab === 'certificates' && (
                <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '20px' }}>
                  <h5 style={{ color: colors.navy, marginBottom: '16px' }}>Certificates & Roster</h5>

                  {/* Template Upload */}
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, color: colors.navy, fontSize: '13px' }}>Certificate Template</span>
                      <span style={{ fontSize: '11px', color: '#888' }}>
                        {(selected as any)?.certificate_template_url ? 'Template uploaded' : 'No template — upload PDF'}
                      </span>
                    </div>
                    <input type="file" accept=".pdf" id="cert-template-upload" style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !selected) return;
                        const formData = new FormData();
                        formData.append('template', file);
                        try {
                          const res = await axiosInstance.post(`/events/${selected.id}/certificates/upload-template`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (res.data.success) {
                            window.alert('Template uploaded successfully!');
                            fetchEvents();
                          }
                        } catch (err: any) { window.alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
                      }}
                    />
                    <button onClick={() => document.getElementById('cert-template-upload')?.click()}
                      style={{ background: (selected as any)?.certificate_template_url ? '#27ae60' : colors.gold, color: (selected as any)?.certificate_template_url ? 'white' : colors.navy, border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginRight: '8px' }}>
                      {(selected as any)?.certificate_template_url ? 'Replace Template' : 'Upload Template PDF'}
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <button onClick={async () => {
                      if (!selected) return;
                      try {
                        const res = await axiosInstance.post(`/events/${selected.id}/certificates/generate`);
                        if (res.data.success) {
                          window.alert(`Generated ${res.data.generated} certificates!`);
                          fetchGeneratedCerts();
                        }
                      } catch (err: any) { window.alert('Error: ' + (err.response?.data?.error || err.message)); }
                    }}
                      style={{ background: colors.navy, color: 'white', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                      Generate All Certificates
                    </button>

                    <button onClick={async () => {
                      if (!selected) return;
                      try {
                        const res = await axiosInstance.post(`/events/${selected.id}/certificates/send`);
                        if (res.data.success) window.alert(`Sent ${res.data.sent} certificates! (${res.data.failed} failed)`);
                      } catch (err: any) { window.alert('Error: ' + (err.response?.data?.error || err.message)); }
                    }}
                      style={{ background: '#27ae60', color: 'white', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                      Email All Certificates
                    </button>

                    <button onClick={() => {
                      if (!selected) return;
                      window.open(`/api/events/${selected.id}/certificates/download`, '_blank');
                    }}
                      style={{ background: colors.gold, color: colors.navy, border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                      Download All (PDF)
                    </button>

                    <button onClick={() => {
                      if (!selected) return;
                      window.open(`/api/events/${selected.id}/roster/download`, '_blank');
                    }}
                      style={{ background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                      Download Sign-In Roster
                    </button>
                  </div>

                  {/* Participant List */}
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    {registrations.filter((r: any) => r.status !== 'cancelled').length} registered participants will receive certificates
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {registrations.filter((r: any) => r.status !== 'cancelled').map((reg: any, i: number) => (
                      <div key={reg.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: i % 2 === 0 ? '#f8f9fa' : 'white', borderRadius: '4px', fontSize: '13px' }}>
                        <div>
                          <span style={{ fontWeight: 600, color: colors.navy }}>{reg.full_name}</span>
                          <span style={{ color: '#888', marginLeft: '8px' }}>{reg.email}</span>
                        </div>
                        <span style={{ color: '#888', fontSize: '11px' }}>{reg.company || ''}</span>
                      </div>
                    ))}
                  </div>

                  {/* KOPS-564 — Generated Certificates list (presigned download URLs) */}
                  <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h6 style={{ color: colors.navy, fontSize: '14px', fontWeight: 700, margin: 0 }}>
                        Generated Certificates {generatedCerts.length > 0 ? `(${generatedCerts.length})` : ''}
                      </h6>
                      <button onClick={fetchGeneratedCerts} disabled={loadingCerts}
                        style={{ background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' }}>
                        {loadingCerts ? 'Loading…' : '⟳ Refresh'}
                      </button>
                    </div>

                    {combinedCert && (
                      <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffd54f', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: colors.navy }}>
                          📄 <strong>Combined PDF</strong> (all certificates in one file) — generated {new Date(combinedCert.generatedAt).toLocaleString()}
                        </span>
                        <a href={combinedCert.downloadUrl} target="_blank" rel="noopener noreferrer"
                          style={{ background: colors.gold, color: colors.navy, fontSize: '12px', fontWeight: 700, textDecoration: 'none', padding: '6px 14px', borderRadius: '4px' }}>
                          Download Combined
                        </a>
                      </div>
                    )}

                    {loadingCerts && generatedCerts.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#888', padding: '12px', textAlign: 'center' }}>Loading certificates…</div>
                    ) : generatedCerts.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#888', padding: '16px', background: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                        No certificates generated yet. Click "Generate All Certificates" above to create them.
                      </div>
                    ) : (
                      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                        {generatedCerts.map((cert: any, i: number) => (
                          <div key={cert.participantId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: i % 2 === 0 ? '#f8f9fa' : 'white', fontSize: '13px', borderBottom: i < generatedCerts.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: colors.navy }}>{cert.name}</div>
                              <div style={{ fontSize: '11px', color: '#888' }}>
                                {cert.email || '(no email on file)'} · Generated {new Date(cert.generatedAt).toLocaleString()} · {(cert.sizeBytes / 1024).toFixed(0)} KB
                              </div>
                            </div>
                            <a href={cert.downloadUrl} target="_blank" rel="noopener noreferrer"
                              style={{ background: colors.navy, color: 'white', fontSize: '11px', fontWeight: 600, textDecoration: 'none', padding: '6px 12px', borderRadius: '4px', marginLeft: '8px' }}>
                              View / Download
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: '#aaa', marginTop: '8px', textAlign: 'right' }}>
                      Download links expire after 1 hour for security. Click ⟳ Refresh to renew.
                    </div>
                  </div>
                </div>
              )}

              {/* Sponsors Tab */}
              {activeDetailTab === 'sponsors' && (
                <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>Event Sponsors</h6>
                    <Button size="sm" onClick={() => setShowAddSponsor(!showAddSponsor)}
                      style={{ background: showAddSponsor ? '#eee' : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px', color: showAddSponsor ? '#666' : colors.navy }}>
                      {showAddSponsor ? '✕ Cancel' : '+ Add Sponsor'}
                    </Button>
                  </div>

                  {showAddSponsor && (
                    <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '12px', border: '1px solid #e9ecef' }}>
                      {sponsorDirectory.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <select
                            onChange={e => {
                              if (!e.target.value) return;
                              const s = sponsorDirectory[parseInt(e.target.value)];
                              setNewSponsor({
                                sponsorName: s.sponsor_name || '',
                                sponsorEmail: s.sponsor_email || '',
                                sponsorPhone: s.sponsor_phone || '',
                                sponsorCompany: s.sponsor_company || '',
                                sponsorRole: s.sponsor_role || 'venue',
                                notes: ''
                              });
                              e.target.value = '';
                            }}
                            style={{ width: '100%', border: '1px solid #C9952A', borderRadius: '4px', padding: '6px 10px', fontSize: '13px', color: '#666', background: '#fff' }}
                            defaultValue=""
                          >
                            <option value="" disabled>Select a previous sponsor...</option>
                            {sponsorDirectory.map((s: any, i: number) => (
                              <option key={i} value={i}>{s.sponsor_name}{s.sponsor_company ? ` — ${s.sponsor_company}` : ''}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <Row className="g-2">
                        <Col md={6}><input placeholder="Contact Name *" value={newSponsor.sponsorName} onChange={e => setNewSponsor({ ...newSponsor, sponsorName: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }} /></Col>
                        <Col md={6}><input placeholder="Company" value={newSponsor.sponsorCompany} onChange={e => setNewSponsor({ ...newSponsor, sponsorCompany: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }} /></Col>
                        <Col md={4}><input placeholder="Email" value={newSponsor.sponsorEmail} onChange={e => setNewSponsor({ ...newSponsor, sponsorEmail: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }} /></Col>
                        <Col md={4}><input placeholder="Phone" value={newSponsor.sponsorPhone} onChange={e => setNewSponsor({ ...newSponsor, sponsorPhone: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }} /></Col>
                        <Col md={4}><select value={newSponsor.sponsorRole} onChange={e => setNewSponsor({ ...newSponsor, sponsorRole: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }}>
                          <option value="venue">Venue Host</option>
                          <option value="title_sponsor">Title Sponsor</option>
                          <option value="food_beverage">Food & Beverage</option>
                          <option value="technology">Technology</option>
                          <option value="marketing">Marketing</option>
                          <option value="other">Other</option>
                        </select></Col>
                        <Col md={12}><input placeholder="Notes (optional)" value={newSponsor.notes} onChange={e => setNewSponsor({ ...newSponsor, notes: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }} /></Col>
                      </Row>
                      <Button size="sm" className="mt-2" onClick={handleAddSponsor} disabled={addingSponsor || !newSponsor.sponsorName}
                        style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                        {addingSponsor ? 'Adding...' : 'Add Sponsor'}
                      </Button>
                    </div>
                  )}

                  {sponsors.length === 0 && !showAddSponsor && (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No sponsors added yet. Click "+ Add Sponsor" to get started.</p>
                  )}

                  {sponsors.map((s: any) => (
                    <div key={s.id} style={{ padding: '12px', marginBottom: '8px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: colors.navy }}>{s.sponsor_name}</div>
                          {s.sponsor_company && <div style={{ fontSize: '12px', color: '#666' }}>{s.sponsor_company}</div>}
                          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                            {s.sponsor_email && <span className="me-3">{s.sponsor_email}</span>}
                            {s.sponsor_phone && <span className="me-3">{s.sponsor_phone}</span>}
                            <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600 }}>
                              {(s.sponsor_role || 'other').replace(/_/g, ' ')}
                            </span>
                          </div>
                          {s.notes && <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', fontStyle: 'italic' }}>{s.notes}</div>}
                        </div>
                        <Button size="sm" variant="outline-danger" style={{ fontSize: '10px' }} onClick={() => handleDeleteSponsor(s.id)}>Remove</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Team Tab */}
              {activeDetailTab === 'team' && (
                <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>Event Team (Internal Staff)</h6>

                  {/* Add team member */}
                  <div className="d-flex gap-2 mb-3">
                    <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}
                      style={{ flex: 1, border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }}>
                      <option value="">Select employee...</option>
                      {employees.filter(e => !team.find((t: any) => t.user_id === e.id)).map((e: any) => (
                        <option key={e.id} value={e.id}>{e.user_first_name} {e.user_last_name} ({e.user_email})</option>
                      ))}
                    </select>
                    <select value={selectedTeamRole} onChange={e => setSelectedTeamRole(e.target.value)}
                      style={{ width: '140px', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }}>
                      <option value="lead">Lead</option>
                      <option value="instructor">Instructor</option>
                      <option value="coordinator">Coordinator</option>
                      <option value="staff">Staff</option>
                      <option value="support">Support</option>
                    </select>
                    <Button size="sm" onClick={handleAddTeamMember} disabled={!selectedEmployee}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>
                      + Add
                    </Button>
                  </div>

                  {team.length === 0 && (
                    <p style={{ color: '#999', textAlign: 'center', padding: '16px 0', fontSize: '13px' }}>No team members assigned. Select an employee above.</p>
                  )}

                  {team.map((t: any) => (
                    <div key={t.id} style={{ padding: '10px 12px', marginBottom: '6px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: colors.navy }}>{t.user_first_name} {t.user_last_name}</span>
                        <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>{t.user_email}</span>
                        <span style={{ marginLeft: '8px', background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600 }}>
                          {t.role}
                        </span>
                      </div>
                      <Button size="sm" variant="outline-danger" style={{ fontSize: '10px' }} onClick={() => handleRemoveTeamMember(t.id)}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Email Blast Tab */}
              {activeDetailTab === 'email' && (
                <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>Send Email Blast</h6>

                  {/* Recipients */}
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '8px' }}>Send to:</p>
                  <div className="d-flex gap-3 mb-3 flex-wrap">
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={blastRecipients.sponsors} onChange={e => setBlastRecipients({ ...blastRecipients, sponsors: e.target.checked })} className="me-1" />
                      Sponsors ({sponsors.length})
                    </label>
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={blastRecipients.registrants} onChange={e => setBlastRecipients({ ...blastRecipients, registrants: e.target.checked })} className="me-1" />
                      Registrants ({registrations.length})
                    </label>
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={blastRecipients.team} onChange={e => setBlastRecipients({ ...blastRecipients, team: e.target.checked, ccTeam: false })} className="me-1" />
                      Team as TO ({team.length})
                    </label>
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={blastRecipients.ccTeam} onChange={e => setBlastRecipients({ ...blastRecipients, ccTeam: e.target.checked, team: false })} className="me-1" />
                      Team as CC ({team.length})
                    </label>
                  </div>

                  {/* Direct Email */}
                  <div style={{ marginBottom: '10px' }}>
                    <input placeholder="Direct email (e.g., danak@desertsageseminars.com) — comma-separate multiple" value={directEmails} onChange={e => setDirectEmails(e.target.value)}
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 10px', fontSize: '12px' }} />
                  </div>

                  {/* Quick Templates */}
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Quick Templates:</p>
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <button onClick={() => {
                      if (!selected) return;
                      const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      const promoCode = prompt('Promo code for sponsor discount (leave blank if none):') || '';
                      const originalPrice = selected.ticket_price || 25;
                      setBlastSubject(`You're Invited: ${selected.title} — ${evtDate}`);
                      setBlastBody(
`Hi {{name}},

You're invited to ${selected.title}!

📅 ${evtDate} at ${selected.event_time}
📍 ${selected.location_name}
📌 ${selected.location_address}
${selected.sponsor_name ? `\n🏢 Hosted by ${selected.sponsor_name}` : ''}
${promoCode ? `\n🎟️ Use code ${promoCode} at checkout for your exclusive sponsored rate!` : ''}
${originalPrice > 0 ? `\n💰 Regular price: $${originalPrice}${promoCode ? ' — FREE with your sponsor code!' : ''}` : '\n✅ Free to attend'}

This is a hands-on, interactive session. Space is limited — register today to reserve your seat.

👉 Register now: https://www.kairosiq.ai/events/${selected.id}

<a href="https://www.kairosiq.ai/events/${selected.id}" style="display:inline-block;background:#C9952A;color:#0F1E35;padding:14px 32px;border-radius:6px;text-decoration:none;font-weight:700;font-size:16px;margin:16px 0;">Register Now</a>

Questions? Reply to this email or call (520) 251-9595.

See you there!
Michael Boykin
KairosIQ AI, Inc.`
                      );
                    }} style={{ background: colors.navy, color: colors.gold2, border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      Event Invite
                    </button>
                    <button onClick={() => {
                      if (!selected) return;
                      const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      const promoCode = prompt('Sponsor promo code (leave blank if none):') || '';
                      setBlastSubject(`CE Credits: ${selected.title} — ${evtDate}`);
                      setBlastBody(
`Hi {{name}},

Earn 3 hours of ADRE-approved continuing education credits!

📚 ${selected.title}
📅 ${evtDate} at ${selected.event_time}
📍 ${selected.location_name}
📌 ${selected.location_address}
🎓 Presented by KairosIQ AI, Inc. through Desert Sage Seminars
${selected.sponsor_name ? `\n🏢 Venue provided by ${selected.sponsor_name}` : ''}
${promoCode ? `\n🎟️ Use code ${promoCode} at checkout to attend FREE courtesy of your title company!` : ''}

This course covers Real Estate Legal Issues with a focus on AI ethics, Fair Housing compliance, and practical technology tools you can use immediately.

💰 Course fee: $25 ${promoCode ? `(FREE with code ${promoCode})` : ''}
✅ 3 CE credit hours — ADRE approved

👉 Register now: https://www.kairosiq.ai/events/${selected.id}

<a href="https://www.kairosiq.ai/events/${selected.id}" style="display:inline-block;background:#C9952A;color:#0F1E35;padding:14px 32px;border-radius:6px;text-decoration:none;font-weight:700;font-size:16px;margin:16px 0;">Register Now</a>

Space is limited. Reserve your seat today.

Michael Boykin, M.S.
Licensed Arizona Real Estate Broker
KairosIQ AI, Inc.`
                      );
                    }} style={{ background: colors.navy, color: colors.gold2, border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      CE Course Invite
                    </button>
                    <button onClick={() => {
                      if (!selected) return;
                      const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      setBlastSubject(`Reminder: ${selected.title} is ${evtDate}`);
                      setBlastBody(
`Hi {{name}},

Just a friendly reminder — ${selected.title} is coming up!

📅 ${evtDate} at ${selected.event_time}
📍 ${selected.location_name}
📌 ${selected.location_address}

We're looking forward to seeing you there. If you haven't registered yet, seats are still available:

👉 https://www.kairosiq.ai/events/${selected.id}

See you soon!
Michael Boykin
KairosIQ AI, Inc.`
                      );
                    }} style={{ background: '#eee', color: '#555', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      Reminder
                    </button>
                    <button onClick={() => {
                      if (!selected) return;
                      const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      const sponsorName = prompt('Sponsor company name (e.g., Lawyers Title):') || 'your title company';
                      const promoCode = prompt('Promo code:') || 'CODE';
                      const originalPrice = selected.ticket_price || 150;
                      const discountPrice = prompt('Discounted price (e.g., 20):') || '20';
                      const maxSeats = prompt('Max seats (e.g., 30):') || '30';
                      setBlastSubject(`We Got You Covered — Ready-to-Send AI Workshop Invitation for Your Agents`);
                      setBlastBody(
`Hi {{name}},

Thank you again for sponsoring the ${selected.title} — your agents are going to love this.

Below is a ready-to-send invitation you can forward directly to your agent network. All they need to do is click the link and use code ${promoCode} at checkout to get in for $${discountPrice} instead of $${originalPrice}.

Feel free to send it as-is or personalize the opening line. Everything they need is in the message.

---

Feel free to forward the message below to your agents:

---

Subject: Exclusive Invite — ${selected.title} · Your Ticket Is $${discountPrice}

Hi [Agent Name],

As part of our commitment to bringing you the best tools and training in the market, we have partnered with KairosIQ AI to offer you exclusive access to their ${selected.title} at a special rate.

Regular price is $${originalPrice}. As a ${sponsorName} agent, you pay just $${discountPrice}.

Here is what this workshop is:

A 3-hour hands-on session where you bring your laptop and build real content for your business — listing descriptions, LinkedIn posts, Instagram captions, Facebook posts, TikTok hooks, client emails, and market updates — using ChatGPT and Claude. No slides. No lectures. You leave with everything set up and ready to use.

If you attended the AI Without Fear seminar and wanted to go deeper — this is that class.

📅 ${evtDate} at ${selected.event_time}
📍 ${selected.location_name}
📌 ${selected.location_address}

How to register:

   Step 1 — Click here to register:
   https://www.kairosiq.ai/events/${selected.id}

   Step 2 — At checkout enter code: ${promoCode}

   Step 3 — Your price drops from $${originalPrice} to $${discountPrice}

<a href="https://www.kairosiq.ai/events/${selected.id}" style="display:inline-block;background:#C9952A;color:#0F1E35;padding:14px 32px;border-radius:6px;text-decoration:none;font-weight:700;font-size:16px;margin:16px 0;">Register Now — Use Code ${promoCode}</a>

Space is limited to ${maxSeats} agents. This is intentionally small so every attendee gets personal attention. Reserve your seat before it fills.

---

Let me know if you'd like me to adjust anything or if you need help getting this out quickly.

Michael A. Boykin Jr.
CEO, KairosIQ AI, Inc.
michael@kairosiq.ai · (520) 251-9595`
                      );
                    }} style={{ background: colors.gold, color: colors.navy, border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      Sponsor Kit
                    </button>
                    <button onClick={() => {
                      if (!selected) return;
                      const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      const attended = registrations.filter((r: any) => r.attended || r.status === 'attended');
                      const noShow = registrations.filter((r: any) => r.status === 'no_show');
                      // Anyone not cancelled, not attended, not no-show — i.e. confirmed/registered/null status
                      const registered = registrations.filter((r: any) =>
                        r.status !== 'cancelled' && r.status !== 'no_show' && !r.attended && r.status !== 'attended'
                      );
                      const cancelled = registrations.filter((r: any) => r.status === 'cancelled');
                      const formatList = (list: any[]) => list.length === 0 ? '(none)' : list.map((r: any, i: number) =>
                        `${i+1}. ${r.full_name} — ${r.email}${r.phone ? ` — ${r.phone}` : ''}${r.brokerage ? ` — ${r.brokerage}` : ''}${r.license_number ? ` — License: ${r.license_number}` : ''}`
                      ).join('\n');
                      setBlastSubject(`Event Roster: ${selected.title} — ${evtDate}`);
                      setBlastBody(
`Hi {{name}},

Here is the roster for ${selected.title} on ${evtDate}.

REGISTERED (${registered.length}):
${formatList(registered)}
${attended.length > 0 ? `\nATTENDED (${attended.length}):\n${formatList(attended)}\n` : ''}${noShow.length > 0 ? `\nNO SHOW (${noShow.length}):\n${formatList(noShow)}\n` : ''}${cancelled.length > 0 ? `\nCANCELLED (${cancelled.length}):\n${formatList(cancelled)}\n` : ''}
TOTAL: ${registrations.length} registered${attended.length > 0 ? `, ${attended.length} attended (${Math.round(attended.length / registrations.length * 100)}% attendance rate)` : ''}

Thank you,
KairosIQ AI Support Team`
                      );
                    }} style={{ background: '#2c7a2c', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                      Event Roster
                    </button>
                    {selected?.category === 'ce_course' && (
                      <>
                        <button onClick={() => {
                          if (!selected) return;
                          const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                          const evtLink = `https://www.kairosiq.ai/events/${selected.id}`;
                          setBlastSubject(`New CE Course Scheduled: ${selected.title} — ${evtDate}`);
                          setBlastBody(
`Hi Dana,

We have scheduled a new CE course and need to coordinate marketing and materials.

Course Details:
📚 ${selected.title}
📅 ${evtDate}
🕐 ${selected.event_time}
📍 ${selected.location_name}
📌 ${selected.location_address}
⏱️ ${selected.duration_minutes || 180} minutes
🎓 Instructor: Michael A. Boykin Jr.
${selected.ce_credit_hours ? `📝 CE Credit Hours: ${selected.ce_credit_hours}` : ''}

Event Registration Link:
👉 ${evtLink}

Please confirm the following:
1. Course is set up in the Desert Sage system
2. Marketing link and materials are ready
3. Course is posted on the Desert Sage calendar at $25/seat

Registration link for your website: ${evtLink}

Let me know if you need anything else to get this published.

Thank you,
Michael A. Boykin Jr.
CEO, KairosIQ AI, Inc.
michael@kairosiq.ai · (520) 251-9595`
                          );
                          // Auto-set recipients: clear all, we'll send manually to Dana + CC Mike
                          setBlastRecipients({ sponsors: false, registrants: false, team: false, ccTeam: false });
                          setDirectEmails('danak@desertsageseminars.com, michael@kairosiq.ai');
                        }} style={{ background: '#8B4513', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                          Desert Sage Setup
                        </button>
                        <button onClick={() => {
                          if (!selected) return;
                          const evtDate = new Date(selected.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                          const active = registrations.filter((r: any) => r.status !== 'cancelled');
                          const attendedCount = active.filter((r: any) => r.attended || r.status === 'attended').length;
                          const courseHappened = new Date(selected.event_date) < new Date();
                          const attendanceFinalized = attendedCount > 0;
                          const rosterList = active
                            .map((r: any, i: number) => {
                              const status = (r.attended || r.status === 'attended')
                                ? '[ATTENDED]'
                                : r.status === 'no_show' ? '[NO-SHOW]' : '[REGISTERED]';
                              return `${i + 1}. ${r.full_name} — ${r.email}${r.license_number ? ` — License: ${r.license_number}` : ''} ${status}`;
                            })
                            .join('\n');
                          const subjectPrefix = attendanceFinalized ? 'CE Course Completed' : (courseHappened ? 'CE Course Roster — Awaiting Attendance' : 'CE Course Roster — Pre-Event');
                          const intro = attendanceFinalized
                            ? 'The following CE course has been completed. Please process CE credits for the attendees listed below.'
                            : courseHappened
                              ? 'The following CE course has occurred. Final attendance is being confirmed. Roster below shows registered attendees with current status.'
                              : 'The following CE course is upcoming. Roster below shows currently registered attendees.';
                          setBlastSubject(`${subjectPrefix}: ${selected.title} — ${evtDate} — ${active.length} on roster`);
                          setBlastBody(
`Hi Dana,

${intro}

Course: ${selected.title}
Date: ${evtDate}
Time: ${selected.event_time}
Location: ${selected.location_name}
Instructor: Michael A. Boykin Jr.
${selected.ce_credit_hours ? `CE Credit Hours: ${selected.ce_credit_hours}` : ''}

Total on Roster: ${active.length}${attendanceFinalized ? ` — ${attendedCount} marked attended` : ''}

ROSTER:
${rosterList || '(No registrations yet)'}

${attendanceFinalized
  ? 'Please confirm when CE certificates have been processed and issued.'
  : 'Final attendance will be confirmed after the course; an updated roster will follow if needed.'}

Thank you,
Michael A. Boykin Jr.
CEO, KairosIQ AI, Inc.
michael@kairosiq.ai · (520) 251-9595`
                          );
                          setBlastRecipients({ sponsors: false, registrants: false, team: false, ccTeam: false });
                          setDirectEmails('danak@desertsageseminars.com, michael@kairosiq.ai');
                        }} style={{ background: '#8B4513', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                          Post-Course Roster
                        </button>
                      </>
                    )}
                  </div>

                  {/* Compose */}
                  <input placeholder="Subject" value={blastSubject} onChange={e => setBlastSubject(e.target.value)}
                    style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px', fontSize: '14px', marginBottom: '8px' }} />
                  <textarea placeholder="Email body..." value={blastBody} onChange={e => setBlastBody(e.target.value)} rows={6}
                    style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px', fontSize: '14px', marginBottom: '8px', resize: 'vertical' }} />

                  {/* Attachments */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', color: '#666', padding: '4px 10px', border: '1px dashed #ccc', borderRadius: '4px' }}>
                      📎 Attach files
                      <input type="file" multiple onChange={e => setBlastAttachments(prev => [...prev, ...Array.from(e.target.files || [])])} style={{ display: 'none' }} />
                    </label>
                    {blastAttachments.length > 0 && (
                      <div style={{ marginTop: '6px' }}>
                        {blastAttachments.map((f, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0f0f0', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', marginRight: '4px', marginBottom: '4px' }}>
                            📄 {f.name} ({(f.size / 1024).toFixed(0)}KB)
                            <button onClick={() => setBlastAttachments(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '14px', padding: '0 2px' }}>&times;</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <small style={{ color: '#999' }}>
                      {[
                        blastRecipients.sponsors && `${sponsors.length} sponsors`,
                        blastRecipients.registrants && `${registrations.length} registrants`,
                        (blastRecipients.team || blastRecipients.ccTeam) && `${team.length} team (${blastRecipients.ccTeam ? 'CC' : 'TO'})`
                      ].filter(Boolean).join(' + ') || 'Select recipients above'}
                    </small>
                    <Button onClick={handleSendBlast} disabled={sendingBlast || !blastSubject || !blastBody || (!blastRecipients.sponsors && !blastRecipients.registrants && !blastRecipients.team && !blastRecipients.ccTeam && !directEmails.trim())}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                      {sendingBlast ? 'Sending...' : 'Send Email Blast'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Session Notes Tab */}
              {activeDetailTab === 'notes' && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>Session Notes & AI Context</h6>
                  <p style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>These notes feed into the AI for personalized follow-up. Be specific — what resonated, what questions came up, what the audience wants next.</p>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Session Summary</label>
                    <textarea rows={3} value={sessionNotes.sessionSummary} onChange={e => setSessionNotes({ ...sessionNotes, sessionSummary: e.target.value })}
                      placeholder="What was covered? Key moments? What went well?"
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical' }} />
                  </div>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Questions Asked by Attendees</label>
                    <textarea rows={3} value={sessionNotes.questionsAsked} onChange={e => setSessionNotes({ ...sessionNotes, questionsAsked: e.target.value })}
                      placeholder="What did the audience want to know? Common themes?"
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical' }} />
                  </div>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Audience Reception</label>
                    <select value={sessionNotes.audienceReception} onChange={e => setSessionNotes({ ...sessionNotes, audienceReception: e.target.value })}
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '8px', fontSize: '13px' }}>
                      <option value="">Select...</option>
                      <option value="highly_engaged">Highly Engaged — lots of questions, energy</option>
                      <option value="engaged">Engaged — attentive, good participation</option>
                      <option value="mixed">Mixed — some engaged, some quiet</option>
                      <option value="skeptical">Skeptical — lots of pushback, concerns</option>
                      <option value="low_energy">Low Energy — quiet, minimal interaction</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Key Outcomes & Commitments</label>
                    <textarea rows={2} value={sessionNotes.keyOutcomes} onChange={e => setSessionNotes({ ...sessionNotes, keyOutcomes: e.target.value })}
                      placeholder="Did anyone express interest in workshops? Portal signups? Follow-up meetings?"
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical' }} />
                  </div>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Follow-Up Needed</label>
                    <textarea rows={2} value={sessionNotes.followUpNeeded} onChange={e => setSessionNotes({ ...sessionNotes, followUpNeeded: e.target.value })}
                      placeholder="Action items for the team. Who needs a call? What needs to be sent?"
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical' }} />
                  </div>

                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Course Outline / What Was Actually Presented</label>
                    <textarea rows={3} value={sessionNotes.courseOutline} onChange={e => setSessionNotes({ ...sessionNotes, courseOutline: e.target.value })}
                      placeholder="Topics covered, demos shown, exercises done..."
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical' }} />
                  </div>

                  <Button onClick={handleSaveNotes} disabled={savingNotes}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                    {savingNotes ? <Spinner size="sm" /> : 'Save Session Notes'}
                  </Button>
                </div>
              )}

              {/* Survey Results Tab */}
              {activeDetailTab === 'survey' && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>Survey Results</h6>
                  </div>

                  {surveyLoading ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                  ) : !surveyResults || surveyResults.responses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <p style={{ color: '#999' }}>No survey responses yet.</p>
                      <p style={{ color: '#888', fontSize: '12px' }}>
                        {selectedRegs.size > 0
                          ? `${selectedRegs.size} registrants selected — click "Send Survey" above to send.`
                          : 'Select specific registrants in the Registrations tab, or click "Send Survey" to send to all attendees.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Stats */}
                      <Row className="g-3 mb-4">
                        <Col md={3}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: colors.gold }}>{surveyResults.stats.avgRating}</div>
                            <div style={{ fontSize: '11px', color: '#888' }}>Avg Rating / 5</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{surveyResults.stats.avgNps}</div>
                            <div style={{ fontSize: '11px', color: '#888' }}>Avg NPS / 10</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: colors.green }}>{surveyResults.stats.total}</div>
                            <div style={{ fontSize: '11px', color: '#888' }}>Responses</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: colors.blue }}>
                              {registrations.filter((r: any) => r.attended).length > 0
                                ? Math.round((surveyResults.stats.total / registrations.filter((r: any) => r.attended).length) * 100) + '%'
                                : '—'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>Response Rate</div>
                          </div>
                        </Col>
                      </Row>

                      {/* Individual Responses */}
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {surveyResults.responses.map((r: any) => (
                          <div key={r.id} style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong style={{ fontSize: '13px' }}>{r.contact_name || r.contact_email}</strong>
                              <div>
                                {[1, 2, 3, 4, 5].map(s => (
                                  <span key={s} style={{ color: s <= r.overall_rating ? colors.gold : '#ddd', fontSize: '14px' }}>★</span>
                                ))}
                                {r.would_recommend && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>NPS: {r.would_recommend}/10</span>}
                              </div>
                            </div>
                            {r.most_valuable && <p style={{ fontSize: '12px', color: '#555', margin: '4px 0' }}><strong>Most valuable:</strong> {r.most_valuable}</p>}
                            {r.want_next && <p style={{ fontSize: '12px', color: '#555', margin: '4px 0' }}><strong>Wants next:</strong> {r.want_next}</p>}
                            {r.open_feedback && <p style={{ fontSize: '12px', color: '#555', margin: '4px 0', fontStyle: 'italic' }}>"{r.open_feedback}"</p>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Registrations Tab */}
              {activeDetailTab === 'registrations' && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />Registrations ({registrations.length})
                  </h6>
                  <Button size="sm" variant="outline-primary" onClick={() => setShowAddReg(!showAddReg)}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Add
                  </Button>
                </div>
                <div className="d-flex gap-1 mb-3">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'attended', label: 'Attended' },
                    { key: 'registered', label: 'Registered' },
                    { key: 'confirmed', label: 'Confirmed' },
                    { key: 'no_show', label: 'No Show' },
                    { key: 'cancelled', label: 'Cancelled' }
                  ].map(f => {
                    const count = f.key === 'all' ? registrations.length
                      : f.key === 'attended' ? registrations.filter((r: any) => r.attended).length
                      : registrations.filter((r: any) => !r.attended && r.status === f.key).length;
                    return (
                      <button key={f.key} onClick={() => { setRegFilter(f.key); setSelectedRegs(new Set()); }}
                        style={{
                          background: regFilter === f.key ? colors.gold : '#f0f0f0',
                          color: regFilter === f.key ? colors.navy : '#666',
                          border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer'
                        }}>
                        {f.label} ({count})
                      </button>
                    );
                  })}
                </div>

                {showAddReg && (
                  <form onSubmit={handleAddReg} className="mb-3 p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                    <Row>
                      <Col md={3}><Form.Control required size="sm" placeholder="Name *" value={newReg.fullName} onChange={e => setNewReg({ ...newReg, fullName: e.target.value })} className="mb-2" /></Col>
                      <Col md={3}><Form.Control required size="sm" type="email" placeholder="Email *" value={newReg.email} onChange={e => setNewReg({ ...newReg, email: e.target.value })} className="mb-2" /></Col>
                      <Col md={2}><Form.Control required size="sm" placeholder="Phone *" value={newReg.phone} onChange={e => setNewReg({ ...newReg, phone: e.target.value })} className="mb-2" /></Col>
                      <Col md={2}><Form.Control size="sm" placeholder="Brokerage" value={newReg.brokerage} onChange={e => setNewReg({ ...newReg, brokerage: e.target.value })} className="mb-2" /></Col>
                      <Col md={2}>
                        <Form.Check type="checkbox" label="Email" checked={newReg.sendEmail} onChange={e => setNewReg({ ...newReg, sendEmail: e.target.checked })} style={{ fontSize: '11px' }} className="mb-1" />
                        <Button type="submit" size="sm" variant="primary" disabled={addingReg}>{addingReg ? <Spinner size="sm" /> : 'Add'}</Button>
                      </Col>
                    </Row>
                  </form>
                )}

                {regsLoading ? (
                  <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : registrations.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>No registrations yet</p>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #eee' }}>
                          <th style={{ padding: '8px 4px' }}>
                            <input type="checkbox" checked={selectedRegs.size > 0 && selectedRegs.size === getFilteredRegs().length} onChange={toggleAllRegs} />
                          </th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>#</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Name</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Email</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Phone</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Brokerage</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '8px 4px', color: '#666', fontWeight: 600 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.filter((r: any) => {
                          if (regFilter === 'all') return true;
                          if (regFilter === 'attended') return r.attended;
                          if (regFilter === 'registered') return !r.attended && r.status === 'registered';
                          return r.status === regFilter;
                        }).map((reg: any, i: number) => (
                          <tr key={reg.id} style={{ borderBottom: '1px solid #f0f0f0', background: selectedRegs.has(reg.id) ? '#f0f7ff' : 'transparent' }}>
                            <td style={{ padding: '8px 4px' }}>
                              {reg.status !== 'cancelled' && <input type="checkbox" checked={selectedRegs.has(reg.id)} onChange={() => toggleReg(reg.id)} />}
                            </td>
                            <td style={{ padding: '8px 4px', color: '#999' }}>{i + 1}</td>
                            <td style={{ padding: '8px 4px', fontWeight: 600 }}>
                              <a href={`/admin/contacts?search=${encodeURIComponent(reg.email)}`} style={{ color: '#0d6efd', textDecoration: 'none', cursor: 'pointer' }}
                                title="View contact profile">
                                {reg.full_name}
                              </a>
                            </td>
                            <td style={{ padding: '8px 4px' }}><a href={`mailto:${reg.email}`} style={{ color: colors.blue }}>{reg.email}</a></td>
                            <td style={{ padding: '8px 4px', color: '#666' }}>{reg.phone || '—'}</td>
                            <td style={{ padding: '8px 4px', color: '#666' }}>{reg.brokerage || '—'}</td>
                            <td style={{ padding: '8px 4px' }}>
                              <select value={reg.attended ? 'attended' : reg.status} onChange={e => handleStatusChange(reg.id, e.target.value)}
                                style={{ fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', padding: '2px 4px' }}>
                                <option value="registered">Registered</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="attended">Attended</option>
                                <option value="no_show">No Show</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px 4px', whiteSpace: 'nowrap' }}>
                              <button title="Edit" onClick={() => {
                                const newName = prompt('Full Name:', reg.full_name);
                                if (newName === null) return;
                                const newEmail = prompt('Email:', reg.email);
                                if (newEmail === null) return;
                                const newPhone = prompt('Phone:', reg.phone || '');
                                const newBrokerage = prompt('Brokerage:', reg.brokerage || '');
                                axiosInstance.put(`/events/registration/${reg.id}/edit`, {
                                  fullName: newName, email: newEmail, phone: newPhone || undefined, brokerage: newBrokerage || undefined
                                }).then(() => { selectEvent(selected); setAlert({ type: 'success', message: 'Registration updated' }); })
                                  .catch(() => setAlert({ type: 'danger', message: 'Failed to update' }));
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: '13px', color: '#0d6efd' }}>&#9998;</button>
                              <button title="Delete" onClick={() => {
                                if (!window.confirm(`Delete registration for ${reg.full_name}?`)) return;
                                axiosInstance.delete(`/events/registration/${reg.id}`)
                                  .then(() => { selectEvent(selected); fetchEvents(); setAlert({ type: 'success', message: 'Registration deleted' }); })
                                  .catch(() => setAlert({ type: 'danger', message: 'Failed to delete' }));
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: '13px', color: '#dc3545' }}>&#128465;</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Activity Log Tab */}
              {activeDetailTab === 'activity' && (
                <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>Activity Log</h6>
                  {eventActivities.length === 0 ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No activity recorded yet</p>
                  ) : (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {eventActivities.map((act: any) => {
                        const iconMap: Record<string, string> = {
                          registration: '📝', event_created: '🎉', email_blast: '📧',
                          sponsor_added: '🤝', team_assigned: '👥', session_notes: '📋',
                          status_change: '🔄', survey_sent: '📊'
                        };
                        const colorMap: Record<string, string> = {
                          registration: '#27ae60', event_created: colors.gold, email_blast: '#3498db',
                          sponsor_added: '#e67e22', team_assigned: '#9b59b6', session_notes: '#1abc9c',
                          status_change: '#f39c12', survey_sent: '#2ecc71'
                        };
                        return (
                          <div key={act.id} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{
                              width: '32px', height: '32px', borderRadius: '50%',
                              background: `${colorMap[act.activity_type] || '#ccc'}15`,
                              border: `2px solid ${colorMap[act.activity_type] || '#ccc'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '14px', flexShrink: 0
                            }}>
                              {iconMap[act.activity_type] || '📌'}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '13px', color: colors.navy }}>{act.subject}</div>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{act.description}</div>
                              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                {new Date(act.activity_date || act.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                {act.activity_subtype && <span style={{ marginLeft: '8px', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: '#f0f0f0', color: '#888' }}>{act.activity_subtype}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsAdminPage;
